/**
 * SDK client singleton + accessor — extracted from index.ts so BOTH the hand-written
 * hooks (index.ts) and the codegen-emitted hooks (generated/hooks.generated.ts) share
 * one client. The app calls initApiClient() on login; every hook calls getClient().
 */

import { QueryClient } from '@tanstack/react-query';
import { InferrexClient } from '@inferrex/sdk';

// ── Query Client ──────────────────────────────────────────────
// The single React-Query client for every Inferrex frontend. Lives here (not in the
// generated barrel) so both generated and custom hooks share one runtime.

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        if (error instanceof Error) {
          const msg = error.message;
          const status = (error as { status?: number }).status;
          // A rejected session will not start working on retry, and the retry
          // keeps the signed-in shell up instead of leaving for /login.
          if (status === 401 || /\b401\b/.test(msg) || /unauthorized/i.test(msg) || /valid bearer token/i.test(msg)) {
            return false;
          }
          if (msg.includes('500') || msg.includes('502') || msg.includes('503')
            || msg.includes('Failed to fetch') || msg.includes('NetworkError')
            || msg.includes('ECONNREFUSED')) {
            return false;
          }
        }
        return failureCount < 2;
      },
      // Refetching every query on tab focus causes a thundering-herd against
      // the API every time the user alt-tabs back. The 30s staleTime above
      // already covers "freshness on return" for normal data. Live-updating
      // dashboards opt in explicitly via refetchInterval on individual hooks.
      refetchOnWindowFocus: false,
    },
  },
});

let sdkClient: InferrexClient | null = null;
let currentToken: string | null = null;
let currentBaseUrl = '';

/** Live IdP getter (Entra acquireTokenSilent / Inferrex refresh). Writes used to
 *  send the snapshot from login; MSAL access tokens expire while the SPA chrome
 *  still looks signed-in — POST /v1/settings/api-keys then 401s. */
type TokenProvider = () => Promise<string | null>;
let tokenProvider: TokenProvider | null = null;

export function setTokenProvider(fn: TokenProvider | null): void {
  tokenProvider = fn;
}

/** Prefer a fresh token from the IdP; fall back to the last cached bearer. */
function readStashedAccessToken(): string | null {
  // Published by the Entra provider before React renders, so the first fetches
  // on the redirect back from Microsoft are not tokenless. No DOM lib in this package.
  const storage = (globalThis as { sessionStorage?: { getItem(key: string): string | null } }).sessionStorage;
  if (!storage) return null;
  try {
    return storage.getItem('inferrex.accessToken');
  } catch {
    return null;
  }
}

export async function resolveApiToken(): Promise<string | null> {
  if (tokenProvider) {
    try {
      const fresh = await tokenProvider();
      if (fresh) currentToken = fresh;
    } catch {
      // Keep the cached bearer — a failed silent acquire must not wipe a still-valid token.
    }
  }
  if (!currentToken) {
    const stashed = readStashedAccessToken();
    if (stashed) currentToken = stashed;
  }
  return currentToken;
}

// Keys match packages/ui/src/state/session-expired.ts. A 2xx means the session
// stuck, so a later real expiry is allowed to send the browser to /login again.
const REAUTH_FLAG = 'inferrex.reauth';
const REAUTH_AT = 'inferrex.reauthAt';

export function clearBrowserReauthGuard(): void {
  // api-client is typechecked without the DOM lib, so reach sessionStorage through globalThis.
  const storage = (globalThis as { sessionStorage?: { removeItem(key: string): void } }).sessionStorage;
  if (!storage) return;
  try {
    storage.removeItem(REAUTH_FLAG);
    storage.removeItem(REAUTH_AT);
  } catch {
    // ignore
  }
}

// ── Ambient environment (task 72) ─────────────────────────────
// Which environment the user is working IN, sent as `X-Environment-Id` on every request.
// It lives here, beside the token, because BOTH fetch paths need it — the SDK's bearerFetch
// below and the hand-written apiFetch in hooks.custom.ts. Two copies would mean the pipelines
// list and the header could be looking at different environments, which is the exact failure
// making the environment ambient is meant to remove.
//
// Null means "let the server decide" — the workspace default. That is the correct starting
// state, not a fallback: a user who has never touched the switcher should be in their default
// environment, and the server already knows which that is.

const ENVIRONMENT_STORAGE_KEY = 'inferrex.activeEnvironmentId';

let activeEnvironmentId: string | null = readStored(ENVIRONMENT_STORAGE_KEY);

/**
 * localStorage is unavailable in a private window, in some embedded webviews, and whenever the
 * browser is set to block site data — and it THROWS rather than returning null there. An
 * unguarded read would take down app boot for a stored preference, so every access is wrapped.
 *
 * Typed structurally rather than as `Storage`, and reached off `globalThis`, because this
 * package's tsconfig deliberately carries no DOM lib (`ES2022` only — `fetch` and `Headers` come
 * from Node's globals). Adding DOM here to type three calls would re-resolve `fetch`/`Headers`
 * for the whole package, which is a much larger change than a preference read deserves.
 */
interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function browserStorage(): KeyValueStore | undefined {
  return (globalThis as { localStorage?: KeyValueStore }).localStorage;
}

function sessionStore(): KeyValueStore | undefined {
  return (globalThis as { sessionStorage?: KeyValueStore }).sessionStorage;
}

function readStored(key: string, store: () => KeyValueStore | undefined = browserStorage): string | null {
  try {
    return store()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string | null, storeOf: () => KeyValueStore | undefined = browserStorage): void {
  try {
    const store = storeOf();
    if (value) store?.setItem(key, value);
    else store?.removeItem(key);
  } catch {
    // Preference not persisted; the in-memory value still applies for this session.
  }
}

/**
 * Pin the active environment, or pass null to fall back to the workspace default.
 *
 * The choice is remembered across reloads. It is NOT validated here — the server ignores an
 * environment the caller's workspace does not own and answers `pinned: false`, which is the
 * signal to call this with null and stop sending a header nothing honours.
 */
export function setActiveEnvironmentId(id: string | null): void {
  activeEnvironmentId = id;
  writeStored(ENVIRONMENT_STORAGE_KEY, id);
}

/** The pinned environment id, or null when the server's default applies. */
export function currentActiveEnvironmentId(): string | null {
  return activeEnvironmentId;
}

/** The header name both fetch paths set. One definition, so they cannot drift apart. */
export const ENVIRONMENT_HEADER = 'X-Environment-Id';

// ── Active workspace (DEBT-56) ────────────────────────────
// The SAME mechanism one level up, and the reason it is here rather than in its own module:
// `pickActiveWorkspace` has honoured X-Workspace-Id since the membership work shipped, and the
// frontend simply never sent one — so a user with two memberships always landed on
// memberships[0], ordered owner-first then oldest-first. Harmless while everyone had one
// workspace; DEBT-55 makes second workspaces easy to acquire by accident.

const WORKSPACE_STORAGE_KEY = 'inferrex.activeWorkspaceId';

let activeWorkspaceId: string | null = readStored(WORKSPACE_STORAGE_KEY);

/** The header name both fetch paths set for the active workspace. */
export const WORKSPACE_HEADER = 'X-Workspace-Id';

/**
 * Pin the active workspace, or null to let the server's default membership ordering apply.
 *
 * Not validated here: the server REFUSES a workspace the caller is not a member of (403
 * `workspace_not_member`, 2026-10-10 — it used to fall back silently), and `contextFetch` below
 * drops such a pin when the server says so.
 */
export function setActiveWorkspaceId(id: string | null): void {
  activeWorkspaceId = id;
  writeStored(WORKSPACE_STORAGE_KEY, id);
}

/** The pinned workspace id, or null when the server's default applies. */
export function currentActiveWorkspaceId(): string | null {
  return activeWorkspaceId;
}

/**
 * Apply the ambient context headers to an outgoing request.
 *
 * ⚠️ **Workspace and environment must travel TOGETHER, on every request, from one place.**
 * They are nested — an environment belongs to a workspace — so a request carrying a stale
 * environment from the workspace just left is the one combination that must never happen.
 * Applying them in a single function means neither fetch path can pick up one and forget the
 * other. (Switching workspace clears the environment pin for the same reason; see
 * `useSwitchWorkspace`.)
 */
export function applyContextHeaders(headers: Headers): void {
  if (activeWorkspaceId) headers.set(WORKSPACE_HEADER, activeWorkspaceId);
  if (activeEnvironmentId) headers.set(ENVIRONMENT_HEADER, activeEnvironmentId);
  if (tenantEntryToken) headers.set(TENANT_ENTRY_HEADER, tenantEntryToken);
}

// ── Tenant entry (tenant-scoped sign-in, 2026-10-10) ─────────────────────────
// When the person names a tenant (its link /t/<id>, or the workspace-ID box), the server records an
// entry grant and returns a token for it. The token lives in sessionStorage — it belongs to THIS
// browser session and ends with it — and rides on every request so a tenant that requires a named
// sign-in serves this session. The token is meaningless without this person's bearer token.

const TENANT_ENTRY_STORAGE_KEY = 'inferrex.tenantEntry';

/** The header both fetch paths send the entry token in. */
export const TENANT_ENTRY_HEADER = 'X-Tenant-Entry';

let tenantEntryToken: string | null = readStored(TENANT_ENTRY_STORAGE_KEY, sessionStore);

/** Store (or clear) this browser session's tenant-entry token. */
export function setTenantEntryToken(token: string | null): void {
  tenantEntryToken = token;
  writeStored(TENANT_ENTRY_STORAGE_KEY, token, sessionStore);
}

/** The current tenant-entry token, or null. */
export function currentTenantEntryToken(): string | null {
  return tenantEntryToken;
}

/** The `error` code the API answers when X-Workspace-Id names a workspace the caller is not in. */
export const WORKSPACE_NOT_MEMBER = 'workspace_not_member';
/** The `error` code for a member of a tenant that requires a named sign-in this session lacks. */
export const TENANT_ENTRY_REQUIRED = 'tenant_entry_required';

async function isRefusedWorkspacePin(response: Response): Promise<boolean> {
  if (response.status !== 403) return false;
  try {
    const body = await response.clone().json() as { error?: unknown };
    return body.error === WORKSPACE_NOT_MEMBER || body.error === TENANT_ENTRY_REQUIRED;
  } catch {
    return false;
  }
}

type FetchInput = Parameters<typeof globalThis.fetch>[0];
type FetchInit = NonNullable<Parameters<typeof globalThis.fetch>[1]>;

/**
 * Send a request whose headers already carry the ambient context (applyContextHeaders), failing
 * CLOSED when the server refuses the pinned workspace. All three fetch paths (the SDK's bearerFetch,
 * `apiFetch`, the platform app's rest-binding `authFetch`) go through this, so they react alike.
 *
 * On 403 `workspace_not_member` — the membership was removed, or the pin came from another
 * session — or `tenant_entry_required` (the pinned tenant now requires a named sign-in this session
 * did not make), the stale workspace pin and the environment pin under it are dropped and every query is
 * invalidated, so the app re-resolves under the server's default workspace. A READ is then retried
 * once without the pins. A WRITE is not: it was aimed at a workspace the caller cannot act in, and
 * replaying it into a different workspace is exactly the silent fallback the server stopped doing.
 * The 403 is returned and surfaces as the request's error.
 */
export async function contextFetch(input: FetchInput, init: FetchInit & { headers: Headers }): Promise<Response> {
  const pinned = init.headers.get(WORKSPACE_HEADER);
  const response = await globalThis.fetch(input, init);
  if (!pinned || !(await isRefusedWorkspacePin(response))) return response;

  if (activeWorkspaceId === pinned) {
    setActiveWorkspaceId(null);
    setActiveEnvironmentId(null);
    void queryClient.invalidateQueries();
  }
  const method = (init.method ?? 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') return response;
  const headers = new Headers(init.headers);
  headers.delete(WORKSPACE_HEADER);
  headers.delete(ENVIRONMENT_HEADER);
  return globalThis.fetch(input, { ...init, headers });
}

// ── Idempotency keys (safe write retries) ─────────────────────────────────────
// The API replays the first answer to a write sent again with the same `Idempotency-Key`
// (services/api/src/middleware/idempotency.ts). The generated mutation hooks send one on every
// route that accepts it: a fresh key per mutate() by default, or the page's own key, which a page
// reuses when the person retries the SAME operation after a retryable failure.

/** What a generated mutation hook accepts. Pass a getter so the page decides per call. */
export interface MutationRequestOptions {
  /** Return the key for this attempt. Omit to send a fresh key on every mutate(). */
  readonly idempotencyKey?: (() => string) | undefined;
}

interface RandomSource {
  randomUUID?: () => string;
  getRandomValues<T extends Uint8Array>(array: T): T;
}

/**
 * A new random key (a v4 UUID). `crypto.randomUUID` exists only in secure contexts, so a page
 * served over plain http on a local network falls back to getRandomValues — still random, never
 * Math.random, because two people's keys must not collide.
 */
export function newIdempotencyKey(): string {
  const c = (globalThis as { crypto?: RandomSource }).crypto;
  if (!c) throw new Error('No crypto source: cannot create an Idempotency-Key');
  if (typeof c.randomUUID === 'function') return c.randomUUID();
  const b = c.getRandomValues(new Uint8Array(16));
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40;
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** The key a generated mutation sends for one attempt: the page's, or a fresh one. */
export function idempotencyKeyFor(options: MutationRequestOptions | undefined): string {
  return options?.idempotencyKey?.() ?? newIdempotencyKey();
}

export function initApiClient(token: string, baseUrl: string): InferrexClient {
  currentToken = token;
  currentBaseUrl = baseUrl;

  // Wrap fetch to inject Authorization: Bearer header.
  // The API checks Bearer first (Entra / Inferrex JWT), then X-API-Key (DB lookup).
  // The frontend always authenticates via the managed IdP bearer token.
  const bearerFetch: typeof globalThis.fetch = async (input, init) => {
    const headers = new Headers(init?.headers);
    const tokenNow = await resolveApiToken();
    if (tokenNow) {
      headers.set('Authorization', `Bearer ${tokenNow}`);
    }
    applyContextHeaders(headers);
    const response = await contextFetch(input, { ...init, headers });
    if (response.ok) clearBrowserReauthGuard();
    return response;
  };

  sdkClient = new InferrexClient({
    apiKey: 'managed-idp-bearer',  // Not used — Bearer header takes priority
    baseUrl,
    timeoutMs: 90_000,
    fetch: bearerFetch,
  });
  return sdkClient;
}

export function updateApiToken(token: string): void {
  currentToken = token;
}

export function getClient(): InferrexClient {
  if (!sdkClient) {
    throw new Error('API client not initialised — call initApiClient first');
  }
  return sdkClient;
}

/** Current bearer token + base URL — read by the hand-written `apiFetch` in index.ts. */
export function currentApiToken(): string | null {
  return currentToken;
}
export function currentApiBaseUrl(): string {
  return currentBaseUrl;
}
